import { Fragment } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  QUOTE_BLOCK_LABELS,
  QUOTE_BLOCK_TYPES,
  emphasisRuns,
  listLevels,
  listMarkers,
  type QuoteBlock,
  type QuoteBlockType,
  type QuoteListBlock,
} from '@t3/shared';

// The SOW body editor: an ordered list of blocks, the same four the house
// SOW format uses.
//
// Lists are edited as one textarea, one item per line, rather than as a
// row of inputs with add/remove buttons per bullet. Writing scope is
// typing, and a list of eight bullets should be eight lines typed
// straight through - not eight clicks of "add another". The line split is
// the only transformation, and it is reversible, so nothing is lost by
// moving between the two representations.

interface Props {
  blocks: QuoteBlock[];
  onChange: (blocks: QuoteBlock[]) => void;
  /** Distinguishes the aria labels when several editors are on one page. */
  labelPrefix: string;
}

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

const PLACEHOLDERS: Record<QuoteBlockType, string> = {
  paragraph: 'What we will do, in the client’s language.',
  subheading: 'A sub-section heading',
  bullets: 'One item per line.\nAnother item.',
  numbered: 'First step.\nSecond step.',
};

export function emptyBlock(type: QuoteBlockType): QuoteBlock {
  return type === 'bullets' || type === 'numbered'
    ? { type, items: [] }
    : { type, text: '' };
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/** Lists are held as items but edited as text: one item per line. */
function blockText(block: QuoteBlock): string {
  return block.type === 'bullets' || block.type === 'numbered'
    ? block.items.join('\n')
    : block.text;
}

function withText(block: QuoteBlock, value: string): QuoteBlock {
  if (block.type === 'bullets' || block.type === 'numbered') {
    return { type: block.type, items: value.split('\n') };
  }
  return { type: block.type, text: value };
}

export function QuoteBlockEditor({ blocks, onChange, labelPrefix }: Props): React.ReactElement {
  return (
    <div className="space-y-2">
      {blocks.map((block, index) => (
        <div
          key={index}
          className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] p-2"
        >
          <div className="mb-1.5 flex items-center gap-2">
            <select
              value={block.type}
              onChange={(e) => {
                const next = [...blocks];
                // Text carries across a type change so switching a
                // paragraph to bullets keeps what was typed; the line
                // split does the rest.
                next[index] = withText(emptyBlock(e.target.value as QuoteBlockType), blockText(block));
                onChange(next);
              }}
              className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs font-medium text-[var(--color-body)]"
              aria-label={`${labelPrefix} block ${index + 1} type`}
            >
              {QUOTE_BLOCK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {QUOTE_BLOCK_LABELS[t]}
                </option>
              ))}
            </select>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => onChange(move(blocks, index, index - 1))}
                disabled={index === 0}
                className="rounded p-1 text-[var(--color-muted)] hover:bg-white hover:text-[var(--color-header)] disabled:opacity-30"
                title="Move up"
                aria-label={`Move ${labelPrefix} block ${index + 1} up`}
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onChange(move(blocks, index, index + 1))}
                disabled={index === blocks.length - 1}
                className="rounded p-1 text-[var(--color-muted)] hover:bg-white hover:text-[var(--color-header)] disabled:opacity-30"
                title="Move down"
                aria-label={`Move ${labelPrefix} block ${index + 1} down`}
              >
                <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onChange(blocks.filter((_, i) => i !== index))}
                className="rounded p-1 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                title="Remove block"
                aria-label={`Remove ${labelPrefix} block ${index + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {block.type === 'subheading' ? (
            <input
              value={blockText(block)}
              onChange={(e) => {
                const next = [...blocks];
                next[index] = withText(block, e.target.value);
                onChange(next);
              }}
              className={`${inputClass} font-semibold`}
              placeholder={PLACEHOLDERS[block.type]}
              aria-label={`${labelPrefix} block ${index + 1} text`}
            />
          ) : (
            <textarea
              rows={block.type === 'paragraph' ? 4 : 3}
              value={blockText(block)}
              onChange={(e) => {
                const next = [...blocks];
                next[index] = withText(block, e.target.value);
                onChange(next);
              }}
              className={inputClass}
              placeholder={PLACEHOLDERS[block.type]}
              aria-label={`${labelPrefix} block ${index + 1} text`}
            />
          )}

          {(block.type === 'bullets' || block.type === 'numbered') && (
            <p className="mt-1 text-xs text-[var(--color-muted)]">One item per line.</p>
          )}
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        {QUOTE_BLOCK_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange([...blocks, emptyBlock(t)])}
            className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs font-medium text-[var(--color-t3-blue)] hover:border-[var(--color-t3-blue)]"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            {QUOTE_BLOCK_LABELS[t]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Text with its **bold** runs drawn bold. Everything else is literal. */
export function Emphasis({ text }: { text: string }): React.ReactElement {
  return (
    <>
      {emphasisRuns(text).map((run, i) =>
        run.bold ? (
          <strong key={i} className="font-bold">
            {run.text}
          </strong>
        ) : (
          <Fragment key={i}>{run.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * A list, nested the way a contract numbers its clauses: 1. a. i.
 *
 * The markers are drawn from `listMarkers` rather than by the browser's
 * list-style, so the page, the editor and the plain-text rendering on
 * the project ticket all number a nested item the same way.
 */
export function QuoteListView({
  block,
  className = '',
  markerClassName = '',
  itemClassName = '',
}: {
  block: QuoteListBlock;
  className?: string;
  markerClassName?: string;
  itemClassName?: string;
}): React.ReactElement | null {
  const levels = listLevels(block);
  const markers = listMarkers(block);
  const rows = block.items
    .map((item, i) => ({ item, level: levels[i]!, marker: markers[i]! }))
    .filter((row) => row.item.trim() !== '');
  if (rows.length === 0) return null;
  const Tag = block.type === 'numbered' ? 'ol' : 'ul';
  return (
    <Tag className={`list-none ${className}`}>
      {rows.map((row, i) => (
        <li
          key={i}
          className={`grid grid-cols-[1.6em_1fr] gap-x-[0.4em] ${itemClassName}`}
          style={{ marginLeft: `${(row.level - 1) * 1.6}em` }}
        >
          <span aria-hidden="true" className={`text-right ${markerClassName}`}>
            {row.marker}
          </span>
          <span className="whitespace-pre-line">
            <Emphasis text={row.item} />
          </span>
        </li>
      ))}
    </Tag>
  );
}

/**
 * Read-only rendering of a section's blocks.
 *
 * Two scales. `compact` is the ticket panel and anywhere the scope is a
 * supporting detail; `document` is a larger reading size. The client's
 * own page draws blocks in its own typography (QuoteDocumentView) with
 * the same `Emphasis` and `QuoteListView` underneath, so bold and
 * numbering cannot differ between them.
 */
export function QuoteBlocksView({
  blocks,
  scale = 'compact',
}: {
  blocks: readonly QuoteBlock[];
  scale?: 'compact' | 'document';
}): React.ReactElement {
  const proseClass =
    scale === 'document'
      ? 'text-[16px] leading-[1.7] text-[var(--color-body)]'
      : 'text-[15px] leading-relaxed text-[var(--color-body)]';
  const listClass = scale === 'document' ? 'space-y-2' : 'space-y-1';
  return (
    <div className={scale === 'document' ? 'space-y-4' : 'space-y-2'}>
      {blocks.map((block, index) => {
        if (block.type === 'subheading') {
          return (
            <h4
              key={index}
              className={
                scale === 'document'
                  ? 'pt-2 text-[17px] font-semibold text-[var(--color-header)]'
                  : 'pt-1 text-sm font-semibold text-[var(--color-header)]'
              }
            >
              <Emphasis text={block.text} />
            </h4>
          );
        }
        if (block.type === 'paragraph') {
          // pre-line, not pre-wrap: a line break inside a paragraph (the
          // notice addresses) is kept, runs of spaces are not.
          return (
            <p key={index} className={`whitespace-pre-line ${proseClass}`}>
              <Emphasis text={block.text} />
            </p>
          );
        }
        return (
          <QuoteListView
            key={index}
            block={block}
            className={`${listClass} ${proseClass}`}
            markerClassName={
              block.type === 'numbered'
                ? 'font-semibold text-[var(--color-t3-blue)]'
                : 'text-[var(--color-t3-blue)]'
            }
          />
        );
      })}
    </div>
  );
}

